// ============================================
// LevantamientoForm.js — captura tecnica del paso "Levantamiento" (CALENDARIO-E1)
// ============================================
// El paso 2 (antes "Visita") es el LEVANTAMIENTO tecnico del sitio. TODO es
// OPCIONAL: se puede guardar vacio (marca el paso hecho) o saltar el paso.
// Guarda en el registro 'visita' (sin backend nuevo): nota humana + datos
// estructurados (jsonb datos). Campos generales + datos rapidos por tipo.
//
// Boton "Programar visita para el levantamiento": por AHORA solo un aviso —
// el calendario llega en la Entrega 2 (el gancho queda listo aqui).

import React, { useState } from 'react';
import { ClipboardList, Camera, DoorClosed, Bell, ShieldAlert, Network, Wrench, CalendarClock, Save } from 'lucide-react';
import { FF, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CL, COLOR_BORDE, COLOR_BEEP_AZUL } from './helpersExpedientes';

const input = {
  width: '100%', padding: '10px 12px', minHeight: 44, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
  fontSize: 16, fontFamily: FF, color: COLOR_OSCURO, outline: 'none', boxSizing: 'border-box',
};
const label = { fontSize: 12, fontWeight: 700, color: COLOR_GRIS, marginBottom: 4, display: 'block' };

// Tipos de trabajo con sus datos rapidos. Todo opcional.
const TIPOS = [
  { id: 'cctv',    label: 'CCTV',            Icono: Camera,      campos: [
    { k: 'camaras',       label: 'N.o de camaras (aprox)', tipo: 'num' },
    { k: 'dias_respaldo', label: 'Dias de respaldo (grabacion en disco local, NO nube)', tipo: 'num' },
  ]},
  { id: 'accesos', label: 'Control de acceso', Icono: DoorClosed, campos: [
    { k: 'puertas', label: 'N.o de puertas', tipo: 'num' },
    { k: 'metodo',  label: 'Metodo', tipo: 'chips', opciones: ['Huella', 'Tarjeta', 'Facial'] },
  ]},
  { id: 'alarmas', label: 'Alarmas',         Icono: Bell,        campos: [
    { k: 'zonas', label: 'N.o de zonas / sensores', tipo: 'num' },
  ]},
  { id: 'antenas', label: 'Antenas anti-hurto', Icono: ShieldAlert, campos: [
    { k: 'salidas', label: 'N.o de salidas', tipo: 'num' },
    { k: 'tecnologia', label: 'Tecnologia', tipo: 'chips', opciones: ['RF', 'AM'] },
  ]},
  { id: 'redes',   label: 'Redes',           Icono: Network,     campos: [
    { k: 'nodos', label: 'N.o de nodos / puntos', tipo: 'num' },
  ]},
  { id: 'otros',   label: 'Otros',           Icono: Wrench,      campos: [
    { k: 'detalle', label: 'Detalle', tipo: 'text' },
  ]},
];

export default function LevantamientoForm({ valorInicial = null, trabajando, onGuardar, onProgramarVisita }) {
  const vi = valorInicial || {};
  const [descripcion, setDescripcion] = useState(vi.descripcion || '');
  const [direccion, setDireccion] = useState(vi.direccion_sitio || '');
  const [contacto, setContacto] = useState(vi.contacto_sitio || '');
  const [tipo, setTipo] = useState(vi.tipo_trabajo || null);
  const [datos, setDatos] = useState(vi.datos_tipo || {}); // { camaras, dias_respaldo, ... }
  const [aviso, setAviso] = useState(null);

  const setCampo = (k, v) => setDatos(d => ({ ...d, [k]: v }));

  const guardar = async () => {
    if (trabajando) return;
    const tipoDef = TIPOS.find(t => t.id === tipo);
    // Nota humana legible (resumen) para el historial.
    const partes = [];
    if (descripcion.trim()) partes.push(descripcion.trim());
    if (direccion.trim()) partes.push(`Sitio: ${direccion.trim()}`);
    if (contacto.trim()) partes.push(`Contacto: ${contacto.trim()}`);
    if (tipoDef) {
      const resumenTipo = tipoDef.campos
        .map(c => (datos[c.k] != null && String(datos[c.k]).trim() !== '') ? `${c.label}: ${datos[c.k]}` : null)
        .filter(Boolean).join(' · ');
      partes.push(`[${tipoDef.label}]${resumenTipo ? ' ' + resumenTipo : ''}`);
    }
    const nota = partes.join('\n') || null;
    // Datos estructurados (jsonb) para el futuro modulo/calendario.
    const estructurado = {
      descripcion: descripcion.trim() || null,
      direccion_sitio: direccion.trim() || null,
      contacto_sitio: contacto.trim() || null,
      tipo_trabajo: tipo || null,
      datos_tipo: tipo ? datos : null,
    };
    await onGuardar({ nota, datos: estructurado });
  };

  const tipoDef = TIPOS.find(t => t.id === tipo);

  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: COLOR_GRIS }}>
        <ClipboardList size={16} color={COLOR_BEEP_AZUL} />
        <span style={{ fontSize: 12.5, lineHeight: 1.4 }}>
          Levantamiento tecnico del sitio. Todo es opcional: llena lo que aplique, o marca el paso listo/saltalo si no se necesita.
        </span>
      </div>

      {/* GENERALES */}
      <div>
        <label style={label}>Descripcion tecnica / notas</label>
        <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value)} rows={3}
          placeholder="Que hay que hacer, condiciones del sitio, observaciones..."
          style={{ ...input, resize: 'vertical', minHeight: 70 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <label style={label}>Direccion exacta del sitio</label>
          <input value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Calle, numero, referencias" style={input} />
        </div>
        <div>
          <label style={label}>Contacto en sitio</label>
          <input value={contacto} onChange={(e) => setContacto(e.target.value)} placeholder="Nombre y telefono" style={input} />
        </div>
      </div>

      {/* FOTOS/ADJUNTOS: preparado para Entrega futura */}
      <div style={{ fontSize: 11.5, color: COLOR_GRIS_CL, fontStyle: 'italic' }}>
        Fotos y archivos del levantamiento: proximamente.
      </div>

      {/* DATOS RAPIDOS POR TIPO */}
      <div>
        <label style={label}>Datos rapidos por tipo (opcional)</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {TIPOS.map(t => {
            const activo = tipo === t.id;
            const Icono = t.Icono;
            return (
              <button key={t.id} type="button" onClick={() => setTipo(activo ? null : t.id)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 44,
                  padding: '8px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: FF,
                  fontSize: 12.5, fontWeight: activo ? 700 : 600,
                  border: `1px solid ${activo ? COLOR_BEEP_AZUL : COLOR_BORDE}`,
                  background: activo ? '#eef4fb' : '#fff', color: activo ? COLOR_BEEP_AZUL : COLOR_GRIS,
                }}>
                <Icono size={15} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tipoDef && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, border: `1px solid ${COLOR_BORDE}`, borderRadius: 10, padding: 12, background: '#fbfcfe' }}>
          {tipoDef.campos.map(c => (
            <div key={c.k}>
              <label style={label}>{c.label}</label>
              {c.tipo === 'chips' ? (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {c.opciones.map(op => {
                    const sel = datos[c.k] === op;
                    return (
                      <button key={op} type="button" onClick={() => setCampo(c.k, sel ? '' : op)}
                        style={{
                          minHeight: 44, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: FF,
                          fontSize: 13, fontWeight: sel ? 700 : 600,
                          border: `1px solid ${sel ? COLOR_BEEP_AZUL : COLOR_BORDE}`,
                          background: sel ? '#eef4fb' : '#fff', color: sel ? COLOR_BEEP_AZUL : COLOR_GRIS,
                        }}>{op}</button>
                    );
                  })}
                </div>
              ) : (
                <input
                  value={datos[c.k] != null ? datos[c.k] : ''}
                  onChange={(e) => setCampo(c.k, c.tipo === 'num' ? e.target.value.replace(/[^\d]/g, '') : e.target.value)}
                  inputMode={c.tipo === 'num' ? 'numeric' : 'text'}
                  placeholder={c.tipo === 'num' ? '0' : ''}
                  style={input}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* PROGRAMAR VISITA (placeholder — Entrega 2) */}
      <button type="button" onClick={() => { if (onProgramarVisita) onProgramarVisita(); else setAviso('El calendario llega en la siguiente entrega.'); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44,
          padding: '10px 14px', borderRadius: 10, cursor: 'pointer', fontFamily: FF, fontSize: 13, fontWeight: 700,
          border: `1px dashed ${COLOR_BEEP_AZUL}`, background: '#f5f9fe', color: COLOR_BEEP_AZUL,
        }}>
        <CalendarClock size={16} /> ¿Programar visita para el levantamiento?
      </button>
      {aviso && (
        <div style={{ fontSize: 12.5, color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 10px' }}>
          {aviso}
        </div>
      )}

      {/* GUARDAR (funciona aunque este vacio: marca el paso hecho) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={guardar} disabled={trabajando}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 44,
            padding: '10px 18px', borderRadius: 10, cursor: trabajando ? 'default' : 'pointer', fontFamily: FF,
            fontSize: 14, fontWeight: 700, border: 'none', background: COLOR_BEEP_AZUL, color: '#fff',
            opacity: trabajando ? 0.6 : 1,
          }}>
          <Save size={15} /> {trabajando ? 'Guardando...' : 'Guardar levantamiento'}
        </button>
      </div>
    </div>
  );
}
